import { SvgIcon, Typography, useTheme, Stack, Box, Grid, Alert } from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { format, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { useDateLocale } from 'src/hooks/use-date-locale';

import { ReactComponent as IconLightbulb } from '../../../../assets/svg/lightbulb.svg';
import { ReactComponent as IconHousekeeping } from '../../../../assets/svg/housekeeping.svg';
import { HousekeepingInspection } from '../../api';

type Props = {
    className?: string;
    statusBadge?: ReactNode;
    inspection: HousekeepingInspection;
    title: string;
    subtitle: string;
    additionalInfo: ReactNode;
    disabledDate?: boolean;
};

export const HousekeepingCard: FC<PropsWithChildren<Props>> = ({
    children,
    className,
    statusBadge,
    inspection,
    title,
    subtitle,
    additionalInfo,
    disabledDate = false,
}) => {
    const { palette, shadows } = useTheme();
    const dateLocale = useDateLocale();

    const now = new Date();
    const inspectionDate = new Date(inspection.inspection_date);
    const optInDeadline = new Date(inspection.opt_in_deadline);
    const { t } = useTranslation();

    const isBeforeOptInDate = isBefore(now, optInDeadline);

    const date = format(inspectionDate, 'd', { locale: dateLocale });
    const month = format(inspectionDate, 'LLL', { locale: dateLocale });

    return (
        <Stack
            alignItems="stretch"
            width="100%"
            className={className}
            css={{
                position: 'relative',
                color: 'inherit',
                textDecoration: 'none',
                background: palette.background.default,
                zIndex: 'auto',

                '::after': {
                    content: '""',
                    position: 'absolute',
                    zIndex: -1,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    top: 0,
                    boxShadow: shadows[1],
                },
            }}
            padding={{ xs: '32px 16px', md: '36px' }}
            gap="32px"
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack
                    direction="row"
                    alignItems="center"
                    gap="16px"
                    css={{
                        textDecoration: 'none',
                        color: palette.common.black,
                    }}
                >
                    <Box>
                        <SvgIcon>
                            <IconHousekeeping
                                css={{
                                    fill: palette.primary.main,
                                }}
                            />
                        </SvgIcon>
                    </Box>

                    <Typography variant="h3" flex={1} textTransform="uppercase">
                        {t('housekeeping')}
                    </Typography>
                </Stack>
                {statusBadge}
            </Stack>
            <Grid container gap="24px" justifyContent="space-between">
                <Grid item>
                    <Box
                        css={{
                            height: '96px',
                            width: '96px',
                            backgroundColor: palette.background.paper,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: `1px solid ${palette.grey[200]}`,
                        }}
                    >
                        <Typography
                            color={disabledDate ? palette.grey[600] : palette.primary.main}
                            fontWeight={700}
                            textTransform="uppercase"
                            css={{ my: '16px' }}
                        >
                            {month}
                        </Typography>
                        <Typography
                            fontWeight={700}
                            variant="h2"
                            {...(disabledDate && { color: palette.grey[600] })}
                        >
                            {date}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item flexGrow={2}>
                    <Stack gap="4px" justifyContent="space-between" height="100%">
                        <Typography
                            color={palette.grey[600]}
                            variant="body2"
                            css={{ textTransform: 'uppercase' }}
                        >
                            {subtitle}
                        </Typography>
                        <Typography
                            variant="h3"
                            fontWeight={700}
                            css={{ textTransform: 'uppercase' }}
                        >
                            {title}
                        </Typography>
                        {additionalInfo}
                    </Stack>
                </Grid>
            </Grid>
            {inspection.is_main_occupant && isBeforeOptInDate ? (
                children
            ) : (
                <Alert
                    severity="warning"
                    css={{
                        backgroundColor: palette.grey[200],
                        padding: '16px',
                        gap: '16px',
                        borderRadius: 0,
                    }}
                    icon={<IconLightbulb />}
                >
                    {isBeforeOptInDate
                        ? t('housekeeping_dual_occupant_info')
                        : t('housekeeping_deadline_passed_info')}
                </Alert>
            )}
        </Stack>
    );
};
